import { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import helpers from "../../../../../utils/helpers";
import url from "@/utils/url";

import {
  setNameFrequency,
  typeOfFinancing,
  planSelected as planSelectedAction,
} from "@/redux/actions/plans";
import { usePlansContext } from "../context/PlansContext";
import { useQuote } from "./useQuote";
import { setIsFirstTimeViewingPlans, showModalLoading } from "@/redux/actions/global";
import { changeGlobalState } from "@/redux/actions/global";

import { useQuoteService } from "@/services/quote.service";
import { CODE_VIDA_CT } from "@/utils/consts";
import { delay, findDefaultObjectValue } from '@/utils/Utils'
import { setSumAssured as setSumAssuredAction } from '@/redux/actions/plans';

export const useLoadInitialData = ({ setShowModalError, initiatingData }) => {
  const planSelected = useSelector((state) => state.plans.planSelected);
  const userData = useSelector((state) => state.vida.userData);
  const currencyPlan = useSelector((state) => state.plans.currencyPlan);
  const financialEntities = useSelector((state) => state.plans.financialEntities);
  const prefetchedSumAssured = useSelector((state) => state.plans.prefetchedSumAssured);
  const isFirstTimeViewingPlans = useSelector((state) => state.global.isFirstTimeViewingPlans);

  const [currency, setCurrency] = useState(currencyPlan);
  const [sumAssured, setSumAssured] = useState("");
  const [savingTime, setSavingTime] = useState("");
  const [initialReturnPercentage, setInitialReturnPercentage] = useState("");
  const [returnPercentage, setReturnPercentage] = useState("");
  const [frequency, setFrequency] = useState();
  const maxAmountQuote = useSelector((state) => state.plans.maxAmountQuote);

  const [financialEntity, setFinancialEntity] = useState("");
  const [arraySumAssured, setArraySumAssured] = useState([]);
  const [arraySavingTime, setArraySavingTime] = useState([]);
  const [arrayReturnPercentage, setArrayReturnPercentage] = useState([]);
  const [arrayFinancialEntities, setArrayFinancialEntities] = useState([]);
  const [arrayPaymentFrequency, setArrayPaymentFrequency] = useState([]);
  const [firstLoad, setFirstLoad] = useState(true);

  const {
    setLoadingQuote,
    setLoadingSavingTime,
    setLoadingReturnPercentage,
    setLoadingPaymentFrequency,
    setLoadingSumAssured,
  } = usePlansContext();

  const { sendQuote } = useQuote({ setShowModalError });
  const { getAttribute, getSuscriptionValidation } = useQuoteService();
  const dispatch = useDispatch();

  useEffect(() => {
  if (userData && Object.keys(userData).length > 0 && currency) {
    setFirstLoad(true);
    setLoadingQuote(true);
    setLoadingPaymentFrequency(true);
    delay(200).then(initData);
  }
}, [currency]);

  useEffect(() => {
    if (planSelected?.idCotizacion) {
      mappingFrequency(frequency);
    }
  }, [frequency, planSelected?.idCotizacion]);

  const initData = async () => {
    initiatingData.current = true;
    setLoadingSumAssured(true);
    dispatch(showModalLoading(""));
    const {
      sumAssured: sumSelected,
      savingTime: savingTimeSelected,
      frequency: frequencySelected,
      returnPercentage: percentageSelected,
      assuredFinancialEntity: financialEntitySelected,
    } = planSelected;

    setArrayFinancialEntities(financialEntities);
    let filterFinancialEntityValue = financialEntities.find(
      (e) => e.idEntidadFinanciera === financialEntitySelected?.numId
    ) ?? {};
    setFinancialEntity(filterFinancialEntityValue);

    // Get data sum assured
    const filterSumAssured = await getSumAssured(sumSelected);
    dispatch(setSumAssuredAction(filterSumAssured));

    if(!filterSumAssured) return;

    // Get data saving time
    const listSavingTime = await getSavingTime();
    const defaultSavingTime =
      listSavingTime.find((e) => e.text === savingTimeSelected?.text) ||
      findDefaultObjectValue(listSavingTime) ||
      listSavingTime[0];
    setSavingTime(defaultSavingTime);

    if(!listSavingTime) return;

    // Get data return percentage
    const listPercentage = await getReturnPercentage(defaultSavingTime);
    const defaultReturnPercentage =
      listPercentage.find((e) => e.text === percentageSelected?.text) ||
      findDefaultObjectValue(listPercentage) ||
      listPercentage[0];
    setReturnPercentage(defaultReturnPercentage);
    setInitialReturnPercentage(defaultReturnPercentage);

    dispatch(
      planSelectedAction({
        ...planSelected,
        sumAssured: filterSumAssured,
        savingTime: defaultSavingTime,
        returnPercentage: defaultReturnPercentage,
        ...(Object.keys(filterFinancialEntityValue).length > 0 && {
          assuredFinancialEntity: {
            numId: filterFinancialEntityValue?.idEntidadFinanciera,
            name: filterFinancialEntityValue?.nombre,
            idTercero: filterFinancialEntityValue?.idTercero,
            razonSocial: filterFinancialEntityValue?.razonSocial,
          },
        }),
      })
    );

    const { currentFrequency, frequencies } = await sendQuote({
      sumAssured: filterSumAssured,
      savingTime: defaultSavingTime,
      returnPercentage: defaultReturnPercentage,
      frequency: frequencySelected || frequency,
    }, () => setLoadingSumAssured(false));

    setFrequency(currentFrequency);
    setArrayPaymentFrequency(frequencies);
    setFirstLoad(false);
    dispatch(setIsFirstTimeViewingPlans(false));
  };

  const getSumAssured = async (sumSelected) => {
    try {
      const payload = {
        codigoAplicacion: CODE_VIDA_CT.APPLICATION,
        codigoProducto: CODE_VIDA_CT.PRODUCT,
        atributo: "SumaAsegurada",
        descAtributo: currency,
      };

      if(isFirstTimeViewingPlans && (!prefetchedSumAssured || prefetchedSumAssured.length === 0)) {
        throw new Error("prefetched sum assured is empty");
      }

      const response = isFirstTimeViewingPlans ? prefetchedSumAssured : await getAttribute(payload, "sumAssured");
      const defaultObj = findDefaultObjectValue(response);

      const sumSelectedFormat = sumSelected?.text ?? defaultObj?.valor;

      const attributeValue = sumSelected?.valor ?? defaultObj?.valor;

      if(!attributeValue) {
        helpers.getTagError(
          "ErrorVidaEndosable",
          "Valor atributo vacío  - Suma Asegurada",
          "trackError",
          url.obtenerAtributoValorVe
        );
        throw new Error("attribute value is empty");
      }

      await getSuscriptionValidation({
        descAtributo: currencyPlan,
        valorAtributo: attributeValue,
      });

      const filterValue =
        response.find((e) => e.text === sumSelectedFormat.replace(" ", "")) ||
        defaultObj;

      setArraySumAssured(response);
      setSumAssured(filterValue);
      return filterValue;
    } catch (error) {
      setArraySumAssured([]);
      dispatch(changeGlobalState(4));
    }
  };

  const mappingFrequency = () => {
    const frequency = planSelected.frequency
    dispatch(setNameFrequency(frequency));
    const montoRetorno = planSelected.montosRetorno?.find(
        (item) => String(item.frecuenciaPago) === frequency
    );
    const montoPagar = planSelected.montosPagar?.find(
        (item) => String(item.frecuenciaPago) === frequency
    );

    dispatch(typeOfFinancing({ ...montoRetorno, ...montoPagar }));
    setLoadingPaymentFrequency(false);
  };

  const resetList = async (currentSumAssured) => {
    const isCreditMaxAmount = Number(currentSumAssured?.value) > maxAmountQuote;
    if (isCreditMaxAmount || initiatingData.current) {
      setLoadingQuote(false);
      return;
    };

    await sendQuote({
      sumAssured: currentSumAssured,
      savingTime,
      returnPercentage,
      frequency,
    });
  };

  const getSavingTime = async () => {
    setLoadingSavingTime(true);
    if (!firstLoad) setLoadingReturnPercentage(true);
    try {
      const payload = {
        codigoAplicacion: CODE_VIDA_CT.APPLICATION,
        codigoProducto: CODE_VIDA_CT.PRODUCT,
        atributo: "PlanVigenciaEndosable",
        descAtributo: currency,
        valorAtributo: userData.fechaNacimiento,
      };
      const response = await getAttribute(payload, "savingTime");
      setArraySavingTime(response);
      return response;
    } catch (error) {
      setArraySavingTime([]);
      return [];
    } finally {
      setLoadingSavingTime(false);
    }
  };

  const getReturnPercentage = async (
    currentSavingTime,
  ) => {
    setLoadingReturnPercentage(true);
    setLoadingQuote(true);

    try {
      const payload = {
        codigoAplicacion: CODE_VIDA_CT.APPLICATION,
        codigoProducto: CODE_VIDA_CT.PRODUCT,
        atributo: "PorcentDevolEndosable",
        descAtributo: currency,
        valorAtributo: currentSavingTime.value,
      }
      const list = await getAttribute(payload, "returnPercentage");
      setArrayReturnPercentage(list);
      return list;
    } catch (error) {
      setArrayReturnPercentage([]);
      return [];
    } finally {
      setLoadingReturnPercentage(false);
    }
  };

  return {
    arraySumAssured,
    arraySavingTime,
    arrayReturnPercentage,
    sumAssured,
    savingTime,
    arrayFinancialEntities,
    arrayPaymentFrequency,
    initialReturnPercentage,
    returnPercentage,
    financialEntity,
    frequency,
    firstLoad,
    setSumAssured,
    setSavingTime,
    setReturnPercentage,
    setFrequency,
    resetList,
    setCurrency,
    getReturnPercentage,
  };
};
